// The creatures lab, `?scene=creatures` (IMP-0059, gap G13): the six creature presets (@rpg-unit/creature-presets)
// built from their rows by @rpg-unit/creature-bodies, in the low-poly look, on a meadow with a gentle rise. The four
// on four legs move on @rpg/figure's four-legged motion, the chicken and the bee on the biped motion. Three scenes,
// switched with `h.creatures.mode(name)` (`&mode=` at boot):
//   - "lineup": all six standing side by side, for judging the bodies;
//   - "field": each on its own loop at its own gait: the dog galloping, the goat trotting, the cow and the cat walking,
//     the hen pecking about, the bee drifting over the flowers;
//   - "poses": the dog sitting and wagging, the cat lying, the goat and the cow grazing;
//   - "side": one creature (`&id=`, default the dog) walking a straight line at `&speed=` (default 0.8 m/s) under a
//     long side-on lens that tracks it, the rest hidden: the view to set beside the walk-cycle comps
//     (ai-context/references/shared-links/2026-10-23-dog-walk-cycles).
// The lab owns only the stage, the loops and the camera: the bodies are the presets', the motion the engine's.
import * as THREE from "three";
import type { ContentRegistry } from "@rpg/content";
import { buildFigure, createMotion, createQuadMotion, type Figure, type Motion, type QuadLocomotion, type QuadMotion } from "@rpg/figure";
import { createLowPolyLook, type LowPolySceneLook, type LowPolyTier } from "@rpg/look";
import { facet, merge, seeded } from "@rpg/meshkit";
import { CREATURE_PRESETS, creatureCard } from "@rpg-unit/creature-presets";
import type { Runtime } from "@rpg/runtime";
import type { ContentTables } from "../content";

/** The meadow: flat in the middle, a gentle rise to the north-east (the slope the four-legged motion pitches to). */
export function meadowHeight(x: number, z: number): number {
  const rise = 0.08 * Math.max(0, x + z - 4);
  return rise + 0.12 * Math.sin(x * 0.35) * Math.cos(z * 0.3) * THREE.MathUtils.smoothstep(Math.hypot(x, z), 3, 8);
}

export const CREATURE_MODES = ["lineup", "field", "poses", "side"] as const;
export type CreatureMode = (typeof CREATURE_MODES)[number];

/** A creature's part in a scene: stand somewhere, or go round a loop at a speed; a pose at rest; a wag. */
interface Part {
  at?: [number, number, number];
  loop?: { x: number; z: number; r: number; speed: number; dir?: 1 | -1 };
  /** Walk a straight line along +x at this speed. */
  line?: number;
  hidden?: boolean;
  pose?: QuadLocomotion["pose"];
  wag?: boolean;
}

/** Each scene's parts and camera (test data for the lab, not game tuning). */
const SCENES: Record<CreatureMode, { parts: Record<string, Part>; eye: [number, number, number]; look: [number, number, number]; fov: number }> = {
  lineup: {
    parts: {
      cow: { at: [-3.3, 0, 0.2] },
      goat: { at: [-1.0, 0, 0.6] },
      dog: { at: [0.6, 0, 0.8], wag: true },
      cat: { at: [1.8, 0, 1.0] },
      chicken: { at: [2.5, 0, 1.1] },
      bee: { at: [2.9, 0, 1.4] },
    },
    eye: [-0.2, 1.7, 7.6],
    look: [-0.3, 0.6, 0.6],
    fov: 44,
  },
  // from the lit side, so the creature faces left as in the comps
  side: { parts: {}, eye: [0, 0.3, -3], look: [0, 0.28, 0], fov: 22 },
  field: {
    parts: {
      dog: { loop: { x: 0, z: 0, r: 6.5, speed: 3.9 } },
      goat: { loop: { x: 0, z: 0, r: 4.6, speed: 2.1, dir: -1 } },
      cow: { loop: { x: -2, z: 1, r: 2.6, speed: 1.0 } },
      cat: { loop: { x: 2.4, z: -1.6, r: 1.4, speed: 0.55, dir: -1 } },
      chicken: { loop: { x: 1.2, z: 2.6, r: 0.9, speed: 0.3 } },
      bee: { loop: { x: -0.6, z: -2.4, r: 0.8, speed: 0.6, dir: -1 } },
    },
    eye: [6, 4.2, 9],
    look: [0, 0.2, 0.4],
    fov: 50,
  },
  poses: {
    parts: {
      dog: { at: [-0.9, 0, 1.2], pose: "sit", wag: true },
      cat: { at: [0.5, 0, 1.6], pose: "lie" },
      goat: { at: [-2.4, 0, -0.2], pose: "eat" },
      cow: { at: [1.6, 0, -1.2], pose: "eat" },
      chicken: { at: [2.5, 0, 1.4] },
      bee: { at: [-1.6, 0, 1.8] },
    },
    eye: [0.6, 1.8, 6.6],
    look: [-0.2, 0.45, 0.2],
    fov: 44,
  },
};

interface Creature {
  id: string;
  fig: Figure;
  quad: QuadMotion | null;
  biped: Motion | null;
  /** Its angle round its loop. */
  angle: number;
}

/** The mounted lab (the same shape the figure lab gives main.ts). */
export interface CreaturesLab {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  offstage: THREE.Scene;
  render(): void;
  state(): { mode: CreatureMode; creatures: Record<string, { gait: string; drift: number; overstretch: number }> };
  handle: { mode(m: CreatureMode): void; report(): ReturnType<CreaturesLab["state"]> };
}

/** The lab when the page asks for it (`?scene=creatures`), else null. */
export function creaturesLabFor(search: string, o: { runtime: Runtime; renderer: THREE.WebGLRenderer; registry: ContentRegistry<ContentTables>; tier: string }): CreaturesLab | null {
  const q = new URLSearchParams(search);
  if (q.get("scene") !== "creatures") return null;
  const first = q.get("mode");
  const side = { id: q.get("id") ?? "dog", speed: Number(q.get("speed") ?? 0.8) };
  return mountCreaturesLab({ ...o, side, mode: (CREATURE_MODES as readonly string[]).includes(first ?? "") ? (first as CreatureMode) : "field" });
}

function mountCreaturesLab(o: { runtime: Runtime; renderer: THREE.WebGLRenderer; registry: ContentRegistry<ContentTables>; tier: string; mode: CreatureMode; side: { id: string; speed: number } }): CreaturesLab {
  const { runtime, renderer, registry } = o;
  const sceneLook = registry.get("looks", "valley-day") as unknown as LowPolySceneLook | undefined;
  if (!sceneLook) throw new Error('creatures lab: no look "valley-day"');
  const tier = registry.get("tiers", o.tier === "low" ? "low" : "high") as unknown as LowPolyTier | undefined;
  if (!tier) throw new Error(`creatures lab: no tier "${o.tier}"`);
  const scene = new THREE.Scene();
  scene.name = "creatures-lab";
  const camera = new THREE.PerspectiveCamera(42, innerWidth / innerHeight, 0.05, 300);
  const look = createLowPolyLook({ renderer, scene, camera, look: sceneLook, tier, shadowExtent: 12 });
  const ground = meadow(look.palette, look.surface);
  scene.add(ground);
  look.track(ground);
  const probe = (x: number, z: number): number => meadowHeight(x, z);

  const creatures = new Map<string, Creature>();
  for (const p of CREATURE_PRESETS) {
    const card = creatureCard(p.id);
    const fig = buildFigure(card, { material: look.surface, colour: look.palette });
    fig.root.name = `creature:${p.id}`;
    scene.add(fig.root);
    look.track(fig.root, { blob: p.plan === "flyer" ? 0 : (p.knobs as { height: number }).height * 0.45 });
    creatures.set(p.id, { id: p.id, fig, quad: card.quad ? createQuadMotion(fig, card.quad, probe) : null, biped: card.quad ? null : createMotion(fig, card.motion!, probe), angle: 0 });
  }

  let mode: CreatureMode = o.mode;
  // the side view's one creature: the rest hidden
  for (const p of CREATURE_PRESETS) SCENES.side.parts[p.id] = p.id === o.side.id ? { line: o.side.speed } : { hidden: true };
  const setMode = (m: CreatureMode): void => {
    mode = m;
    const s = SCENES[m];
    for (const c of creatures.values()) {
      const part = s.parts[c.id]!;
      c.fig.root.visible = !part.hidden;
      c.angle = 0;
      place(c, part, 0);
      c.quad?.reset();
      c.biped?.reset();
      c.quad?.resetDrift();
    }
    camera.position.set(...s.eye);
    camera.lookAt(new THREE.Vector3(...s.look));
    camera.fov = s.fov;
    camera.updateProjectionMatrix();
    look.focus(new THREE.Vector3(...s.look));
  };
  /** Put a creature where its part says, and return its velocity. */
  const place = (c: Creature, part: Part, dt: number): THREE.Vector3 => {
    if (part.line !== undefined) {
      c.angle += part.line * dt;
      const x = -8 + c.angle;
      c.fig.root.position.set(x, probe(x, 0), 0);
      c.fig.root.rotation.y = Math.PI / 2;
      return new THREE.Vector3(part.line, 0, 0);
    }
    if (part.loop) {
      const l = part.loop;
      const dir = l.dir ?? 1;
      c.angle += (dir * l.speed * dt) / l.r;
      const x = l.x + Math.cos(c.angle) * l.r;
      const z = l.z + Math.sin(c.angle) * l.r;
      const tx = -Math.sin(c.angle) * dir;
      const tz = Math.cos(c.angle) * dir;
      c.fig.root.position.set(x, probe(x, z), z);
      c.fig.root.rotation.y = Math.atan2(tx, tz);
      return new THREE.Vector3(tx * l.speed, 0, tz * l.speed);
    }
    const [x, , z] = part.at ?? [0, 0, 0];
    c.fig.root.position.set(x, probe(x, z), z);
    // stand three-quarter on to the camera, facing right
    c.fig.root.rotation.y = mode === "lineup" ? 1.35 : mode === "poses" ? 0.9 : 0;
    return new THREE.Vector3();
  };

  runtime.on(
    "sim",
    (c) => {
      const s = SCENES[mode];
      for (const cr of creatures.values()) {
        const part = s.parts[cr.id]!;
        if (part.hidden) continue;
        const vel = place(cr, part, c.dt);
        const speed = Math.hypot(vel.x, vel.z);
        cr.fig.root.updateMatrixWorld(true);
        if (cr.quad) cr.quad.update(c.dt, { speed, velocity: { x: vel.x, y: 0, z: vel.z }, ...(part.pose ? { pose: part.pose } : {}), wag: part.wag === true });
        else cr.biped!.update(c.dt, { speed, velocity: { x: vel.x, y: 0, z: vel.z }, grounded: true, jump: false, glide: false });
        // the side view's lens tracks its creature
        if (part.line !== undefined) {
          const p = cr.fig.root.position;
          camera.position.set(p.x, p.y + s.eye[1], s.eye[2]);
          camera.lookAt(p.x, p.y + s.look[1], 0);
          look.focus(p);
        }
      }
    },
    { name: "creatures lab: loops" },
  );
  setMode(mode);
  look.warm();
  addEventListener("resize", () => {
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
    look.resize(innerWidth, innerHeight);
  });
  const state = (): ReturnType<CreaturesLab["state"]> => ({
    mode,
    creatures: Object.fromEntries(
      [...creatures.values()].map((c) => {
        const r = c.quad?.report();
        return [c.id, r ? { gait: r.gait, drift: r.drift.max, overstretch: r.overstretch } : { gait: c.biped!.state(), drift: c.biped!.report().drift.max, overstretch: 0 }];
      }),
    ),
  });
  return { scene, camera, offstage: new THREE.Scene(), render: () => look.render(), state, handle: { mode: setMode, report: state } };
}

/** The meadow: a grid of faceted grass, a few flowers round the bee's loop. */
function meadow(palette: (c: string) => THREE.Color, material: THREE.Material): THREE.Mesh {
  const rng = seeded(58);
  const N = 48;
  const S = 80;
  const pos: number[] = [];
  const col: number[] = [];
  const grass = ["grass.a", "grass.b", "grass.c", "grass.d"].map((k) => palette(k));
  const v = (i: number, j: number): [number, number, number] => {
    const x = -S / 2 + (i / N) * S;
    const z = -S / 2 + (j / N) * S;
    return [x, meadowHeight(x, z), z];
  };
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const a = v(i, j);
      const b = v(i + 1, j);
      const c = v(i, j + 1);
      const d = v(i + 1, j + 1);
      for (const tri of [[a, c, b], [b, c, d]]) {
        const k = grass[Math.floor(rng() * grass.length)]!;
        for (const p of tri) {
          pos.push(...p);
          col.push(k.r, k.g, k.b);
        }
      }
    }
  const terrain = new THREE.BufferGeometry();
  terrain.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  terrain.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  terrain.setAttribute("glow", new THREE.Float32BufferAttribute(new Float32Array(pos.length / 3), 1));
  terrain.computeVertexNormals();
  const parts: THREE.BufferGeometry[] = [terrain];
  const flowers = ["#f2d14b", "#e86f8a", "#f4f0ea", "#9b7fd6"];
  for (let i = 0; i < 26; i++) {
    const a = rng() * Math.PI * 2;
    const r = 0.3 + rng() * 1.6;
    const x = -0.6 + Math.cos(a) * r;
    const z = -2.4 + Math.sin(a) * r;
    const y = meadowHeight(x, z);
    parts.push(facet(new THREE.CylinderGeometry(0.008, 0.01, 0.32, 3).translate(x, y + 0.16, z), { colour: palette("grass.c"), rng }));
    parts.push(facet(new THREE.IcosahedronGeometry(0.045, 0).translate(x, y + 0.33, z), { colour: new THREE.Color(flowers[i % flowers.length]!), tint: 0.06, rng }));
  }
  return new THREE.Mesh(merge(parts.map((p) => (p.index ? p.toNonIndexed() : p))), material);
}
